// Helper for mapping excerpts for export
var _ = require('underscore');
import { getAbsoluteExcerptUrl } from '../ExcerptUrlMapper'
import { BOT_USER_ID } from 'constants/UserConstants';

const convertZTimestampToLocal = (timestamp) => {
  const date = new Date(timestamp);
  const localTime = date.toLocaleString();
  return localTime;
};

const getUserName = (userId, userMap) => {
  if (userId === BOT_USER_ID) {
    return 'AI Assistant';
  }
  const user = userMap[userId];
  return user?.name || 'Unknown User';
};

const memoString = (memos, userMap) => {
  return (memos || []).filter((memo)=>!!memo).map((memo)=> {
    const userName = getUserName(memo.user_id, userMap);
    return `${userName}: (${convertZTimestampToLocal(memo.created_at)}) ${memo.text}`;
  }).join('\n')
};

const exportExcerptHelper = (excerpts, descriptors, codesData, usersData, transcripts, selectionsData) => {
  // Handle both array and object with descriptors property
  const descriptorArray = Array.isArray(descriptors) ? descriptors : (descriptors?.descriptors || []);
  const descriptorNames = descriptorArray.map((descriptor) => descriptor.name);
  
  // Create maps for quick lookups
  const transcriptMap = {};
  (transcripts || []).forEach(t => {
    transcriptMap[t.id] = t;
  });
  
  const codeMap = {};
  (codesData || []).forEach(code => {
    codeMap[code.id] = code;
  });
  
  // Handle users data - expects object with owner and collaborators from useProjectUsers
  const userMap = {};
  if (usersData) {
    // Add owner to the map
    if (usersData.owner) {
      userMap[usersData.owner.id] = usersData.owner;
    }
    // Add collaborators to the map
    if (usersData.collaborators && Array.isArray(usersData.collaborators)) {
      usersData.collaborators.forEach(user => {
        userMap[user.id] = user;
      });
    }
  }

  const exportExcerpts = (excerpts || []).map((excerpt) => {
    const transcript = transcriptMap[excerpt.transcript_id] || {};

    // Map descriptor choices for this excerpt's transcript using selections data
    const choices = descriptorArray.map((descriptor) => {
      // If no selections data, return empty string
      if (!selectionsData?.selections_by_transcript) return '';

      // Get selections for this transcript
      const transcriptSelections = selectionsData.selections_by_transcript[excerpt.transcript_id] || [];

      // Find the selection for this descriptor
      const selection = transcriptSelections.find(s => s.multi_choice_descriptor_id === descriptor.id);

      // Return the choice text or empty string
      return selection?.choice || '';
    });

    const memos = memoString(excerpt.memos, userMap);
    
    // Extract unique coders from codings using user map
    const coders = _.chain(excerpt.codings || [])
      .map(coding => getUserName(coding.user_id, userMap))
      .filter(name => !!name && name.length > 0)
      .uniq()
      .value()
      .join('\n');

    // Get unique code names from codings by looking up in codeMap
    const codeNames = _.chain(excerpt.codings || [])
      .map(coding => {
        const code = codeMap[coding.code_id];
        return code?.name || "Untitled Code";
      })
      .uniq()
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .value()

    // One entry per coding (code + coder pair) for the long-form codings
    // export; codeNames/coders above stay aggregated for the snippets export.
    // codedAt stays ISO 8601 (UTC) - that export is built for machines.
    // Dedupe on ids, not display names: code names aren't unique and two
    // departed coders both render as 'Unknown User', which would otherwise
    // collapse distinct codings and undercount rows.
    const codings = _.chain(excerpt.codings || [])
      .uniq(false, (coding) => `${coding.code_id}|${coding.user_id}`)
      .map(coding => {
        const code = codeMap[coding.code_id];
        return {
          codeName: code?.name || "Untitled Code",
          coderName: getUserName(coding.user_id, userMap),
          codedAt: coding.created_at || ''
        };
      })
      .value()
      .sort((a, b) => a.codeName.localeCompare(b.codeName, undefined, { sensitivity: 'base' }))

    return {
      start: excerpt.start || 0,
      end: excerpt.end || 1,
      transcriptName: transcript.name || "Untitled Transcript",
      survey_title: excerpt.survey_title,
      survey_question_text: excerpt.survey_question_text || '',
      survey_row_text: excerpt.survey_row_text || '',
      text: excerpt.text || "",
      url: getAbsoluteExcerptUrl(excerpt),
      memos,
      coders,
      choices,
      codeNames,
      codings
    };
  });

  return {
    excerpts: exportExcerpts,
    descriptorNames
  };
}

export default exportExcerptHelper